import type { BudgetCategory } from './types';

export const PRICING_BUDGET_CATEGORIES = {
  accommodation: 'accommodation',
  activities: 'activities',
  dining: 'dining',
  logistic: 'logistic',
  other: 'other',
} as const satisfies Record<BudgetCategory, BudgetCategory>;

export const PRICING_LEGACY_CATEGORY_LABELS = {
  OTHER: 'Other',
  ATTRACTION: 'Attraction',
  PERSONNEL: 'Personnel',
  TRANSPORTATION: 'Transportation',
  ACCOMMODATION: 'Accommodation',
  VENUE: 'Venue',
  INFORMATION: 'Information',
  DESTINATION: 'Destination',
  DINING: 'Dining',
  ACTIVITY: 'Activity',
} as const;

export const PRICING_LEGACY_SUBCATEGORY_LABELS = {
  HOMESTAY: 'Homestay',
  PACKED_FOOD: 'PackedFood',
  ADMINISTRATIVE_AREA: 'AdministrativeArea',
  POPULAR_SITE: 'PopularSite',
  NATIONAL_PARK: 'NationalPark',
  VAN: 'Van',
  SHOPPING: 'Shopping',
  GIFT: 'Gift',
  GOLF_COURSE: 'GolfCourse',
  APARTMENT: 'Apartment',
  ENTERTAINER: 'Entertainer',
  GUIDE: 'Guide',
  DRIVER_SERVICES: 'DriverServices',
  SPORT: 'Sport',
  TRAIN: 'Train',
  SWIMMING_POOL: 'SwimmingPool',
  INN: 'Inn',
  ACTIVITY: 'Activity',
  CAMP: 'Camp',
  SITE: 'Site',
  FOOD_MARKET: 'FoodMarket',
  AMUSEMENT_PARK: 'AmusementPark',
  COTTAGE: 'Cottage',
  ATTRACTION: 'Attraction',
  SAFARI: 'Safari',
  FERRY: 'Ferry',
  BAR: 'Bar',
  LODGE: 'Lodge',
  CRUISE: 'Cruise',
  OUTDOOR_EQUIPMENT: 'OutdoorEquipment',
  BUNGALOW: 'Bungalow',
  CASTLE: 'Castle',
  HOSTEL: 'Hostel',
  FLIGHT: 'Flight',
  TOUR: 'Tour',
  DINING: 'Dining',
  CULTURAL: 'Cultural',
  MUSEUM: 'Museum',
  LEISURE: 'Leisure',
  EVENT: 'Event',
  TAXI: 'Taxi',
  SPACE_RENTAL: 'SpaceRental',
  POI: 'Poi',
  VILLA: 'Villa',
  CYCLING: 'Cycling',
  BOUTIQUE: 'Boutique',
  RELIGIOUS_SITE: 'ReligiousSite',
  SIGHTSEEING: 'Sightseeing',
  INFORMATION: 'Information',
  COFFEE: 'Coffee',
  GUESTHOUSE: 'Guesthouse',
  BEACH: 'Beach',
  HELICOPTER: 'Helicopter',
  CONFERENCE: 'Conference',
  CAMPING_SITE: 'CampingSite',
  ART_GALLERY: 'ArtGallery',
  ZOO: 'Zoo',
  VENUE: 'Venue',
  WINE: 'Wine',
  CATERING: 'Catering',
  BB: 'BB',
  COOKING_COURSE: 'CookingCourse',
  COUNTRY: 'Country',
  TEAM_ACTIVITIES: 'TeamActivities',
  FESTIVAL: 'Festival',
  BUS: 'Bus',
  MOTORCYCLE: 'Motorcycle',
  AUDIO_VIDEO_EQUIPMENT: 'AudioVideoEquipment',
  BAKERY: 'Bakery',
  GYM: 'Gym',
  RENTAL_CAR: 'RentalCar',
  SPA: 'Spa',
  THEME_PARTY: 'ThemeParty',
  BOAT_ACTIVITY: 'BoatActivity',
  CITY: 'City',
  RESORT: 'Resort',
  HIKING: 'Hiking',
  HOTEL: 'Hotel',
  FISHING_HUNTING: 'FishingHunting',
  TICKET: 'Ticket',
  ECORESORT: 'Ecoresort',
  HOTEL_BOUTIQUE: 'HotelBoutique',
  HOTEL_DE_CHARME: 'HotelDeCharme',
  HOTEL_VILA: 'HotelVila',
  POUSADA: 'Pousada',
  NATURAL_PHENOMENON: 'NaturalPhenomenon',
  NATURE_RESERVE: 'NatureReserve',
  PIZZERIA: 'Pizzeria',
  RESTAURANT: 'Restaurant',
  CITY_GUIDE: 'CityGuide',
  EQUIPMENT: 'Equipment',
  OTHER: 'Other',
  SPEAKER: 'Speaker',
  FIXER: 'Fixer',
  HOLIDAY_VILLAGE: 'HolidayVillage',
  BOAT: 'Boat',
  SEDAN: 'Sedan',
} as const;

export type PricingLegacyCategoryKey =
  keyof typeof PRICING_LEGACY_CATEGORY_LABELS;
export type PricingLegacySubcategoryKey =
  keyof typeof PRICING_LEGACY_SUBCATEGORY_LABELS;

const pricingBudgetCategoryByLegacyCategory = {
  ACCOMMODATION: PRICING_BUDGET_CATEGORIES.accommodation,
  ATTRACTION: PRICING_BUDGET_CATEGORIES.activities,
  ACTIVITY: PRICING_BUDGET_CATEGORIES.activities,
  PERSONNEL: PRICING_BUDGET_CATEGORIES.activities,
  DESTINATION: PRICING_BUDGET_CATEGORIES.activities,
  INFORMATION: PRICING_BUDGET_CATEGORIES.activities,
  DINING: PRICING_BUDGET_CATEGORIES.dining,
  VENUE: PRICING_BUDGET_CATEGORIES.dining,
  TRANSPORTATION: PRICING_BUDGET_CATEGORIES.logistic,
  OTHER: PRICING_BUDGET_CATEGORIES.other,
} as const satisfies Record<PricingLegacyCategoryKey, BudgetCategory>;

const categoryLookup = createLegacyValueLookup(PRICING_LEGACY_CATEGORY_LABELS);
const subcategoryLookup = createLegacyValueLookup(
  PRICING_LEGACY_SUBCATEGORY_LABELS
);

export const resolvePricingLegacyCategoryKey = (
  category: string | null | undefined
): PricingLegacyCategoryKey | null =>
  resolveLegacyKey(category, categoryLookup);

export const resolvePricingLegacySubcategoryKey = (
  subcategory: string | null | undefined
): PricingLegacySubcategoryKey | null =>
  resolveLegacyKey(subcategory, subcategoryLookup);

export const toPricingLegacyCategoryLabel = (
  category: string | null | undefined
): string | null => {
  if (category === null || category === undefined) return null;
  const key = resolvePricingLegacyCategoryKey(category);
  return key ? PRICING_LEGACY_CATEGORY_LABELS[key] : category;
};

export const toPricingLegacySubcategoryLabel = (
  subcategory: string | null | undefined
): string | null => {
  if (subcategory === null || subcategory === undefined) return null;
  const key = resolvePricingLegacySubcategoryKey(subcategory);
  return key ? PRICING_LEGACY_SUBCATEGORY_LABELS[key] : subcategory;
};

export const isPricingAccommodationCategory = (
  category: string | null | undefined
): boolean => resolvePricingLegacyCategoryKey(category) === 'ACCOMMODATION';

export const mapPricingContentCategoryToBudgetCategory = (
  category: string | null | undefined
): BudgetCategory => {
  const key = resolvePricingLegacyCategoryKey(category);
  return key
    ? pricingBudgetCategoryByLegacyCategory[key]
    : PRICING_BUDGET_CATEGORIES.other;
};

function createLegacyValueLookup<TKey extends string>(
  mapping: Record<TKey, string>
): Map<string, TKey> {
  const lookup = new Map<string, TKey>();

  for (const [key, label] of Object.entries(mapping) as Array<[TKey, string]>) {
    lookup.set(normalizePricingContentValue(key), key);
    lookup.set(normalizePricingContentValue(label), key);
  }

  return lookup;
}

function resolveLegacyKey<TKey extends string>(
  value: string | null | undefined,
  lookup: ReadonlyMap<string, TKey>
): TKey | null {
  if (!value) return null;
  return lookup.get(normalizePricingContentValue(value)) ?? null;
}

function normalizePricingContentValue(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}
